import { Pressable } from "react-native";
import { Text } from "react-native-paper";
import type { LevelIconName } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { LevelIcon } from "../LevelIcon";

export type SkillGridItem = {
	id: string;
	label: string;
	icon: LevelIconName;
	disabled?: boolean;
	/** Show lock icon and route to purchase if locked */
	locked?: boolean;
};

type SingleSelectProps = {
	selectionMode?: "single";
	selectedId?: string;
	onSelect: (id: string) => void;
};

type MultiSelectProps = {
	selectionMode: "multiple";
	selectedIds: string[];
	onSelect: (id: string) => void;
};

type Props = {
	skills: SkillGridItem[];
	disabled?: boolean;
} & (SingleSelectProps | MultiSelectProps);

type CellProps = {
	skill: SkillGridItem;
	selected: boolean;
	selectionMode: "single" | "multiple";
	disabled?: boolean;
	onSelect: (id: string) => void;
};

const isSelected = (props: Props, id: string) =>
	props.selectionMode === "multiple"
		? props.selectedIds.includes(id)
		: props.selectedId === id;

const getCellAccessibility = ({
	selectionMode,
	selected,
	isDisabled,
}: {
	selectionMode: "single" | "multiple";
	selected: boolean;
	isDisabled: boolean;
}): {
	accessibilityRole: "checkbox" | "button";
	accessibilityState:
		| { checked: boolean; disabled: boolean }
		| { selected: boolean };
	ariaChecked?: boolean;
} => {
	if (selectionMode === "multiple") {
		return {
			accessibilityRole: "checkbox",
			accessibilityState: { checked: selected, disabled: isDisabled },
			ariaChecked: selected,
		};
	}

	return {
		accessibilityRole: "button",
		accessibilityState: { selected },
	};
};

const SkillCell = ({
	skill,
	selected,
	selectionMode,
	disabled,
	onSelect,
}: CellProps) => {
	const theme = useAppTheme();
	const isDisabled = Boolean(disabled || skill.disabled);
	const accessibility = getCellAccessibility({
		selectionMode,
		selected,
		isDisabled,
	});
	const iconColor = skill.locked
		? theme.colors.onSurfaceVariant
		: selected
			? theme.colors.secondary
			: theme.colors.onSurface;

	return (
		<Pressable
			onPress={() => onSelect(skill.id)}
			disabled={isDisabled}
			accessibilityLabel={skill.label}
			accessibilityRole={accessibility.accessibilityRole}
			accessibilityState={accessibility.accessibilityState}
			// RN Web maps this to aria-checked for Testing Library `.toBeChecked()`.
			aria-checked={accessibility.ariaChecked}
			style={{ flex: 1, opacity: isDisabled ? 0.5 : 1 }}
		>
			<Box
				flex={1}
				alignItems="center"
				justifyContent="center"
				gap={8}
				paddingVertical={16}
				paddingHorizontal={8}
				borderWidth={1}
				borderRadius={theme.roundness * 2}
				borderColor={
					selected ? theme.colors.secondary : theme.colors.surfaceVariant
				}
				backgroundColor={
					selected ? theme.colors.elevation.level5 : "transparent"
				}
			>
				<LevelIcon
					name={skill.locked ? "lock-outline" : skill.icon}
					size={24}
					color={iconColor}
				/>
				<Text
					variant="titleSmall"
					numberOfLines={1}
					style={{ color: theme.colors.onSurface }}
				>
					{skill.label}
				</Text>
			</Box>
		</Pressable>
	);
};

const SkillsGrid = (props: Props) => {
	const { skills, disabled, onSelect } = props;
	const selectionMode =
		props.selectionMode === "multiple" ? "multiple" : "single";

	const handleSelect = (id: string) => {
		const isLastSelected =
			props.selectionMode === "multiple" &&
			props.selectedIds.length === 1 &&
			props.selectedIds[0] === id;
		if (isLastSelected) {
			return;
		}

		onSelect(id);
	};

	return (
		<Box flexDirection="row" gap={8}>
			{skills.map((skill) => (
				<SkillCell
					key={skill.id}
					skill={skill}
					selected={isSelected(props, skill.id)}
					selectionMode={selectionMode}
					disabled={disabled}
					onSelect={handleSelect}
				/>
			))}
		</Box>
	);
};

export default SkillsGrid;
